import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Card,
  Checkbox,
  ColorPicker,
  Input,
  List,
  Modal,
  Space,
  Spin,
  Switch,
  Tooltip,
  message,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  MenuOutlined,
  GlobalOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  SaveOutlined,
  CheckOutlined,
  CloseOutlined,
  CheckSquareOutlined,
  BorderOutlined,
  CompressOutlined,
  ExpandOutlined,
} from '@ant-design/icons'
import B2BLayout from '../../../layouts/b2b'
import fetchAPI from '../../../../lib/utils/fetch-api'
import type { Color } from 'antd/es/color-picker'
import {
  PageContainer,
  Sidebar,
  MapContainer,
  ZoneListItem,
  ZoneHeader,
  ZoneName,
  ZoneMeta,
  ActionButtons,
  Toolbar,
  SearchWrapper,
  EmptyState,
  MapLoadingOverlay,
  DrawingHint,
  EditingHint,
} from './styles'
import { DeliveryZone } from './interfaces'
import { DEFAULT_CITIES, parseAreaToCoordinates } from './utils'

const DeliveryZonesEditor: React.FC = () => {
  // Состояния
  const [zones, setZones] = useState<DeliveryZone[]>([])
  const [filteredZones, setFilteredZones] = useState<DeliveryZone[]>([])
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null)
  const [editingZone, setEditingZone] = useState<DeliveryZone | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [showNoPriceOnly, setShowNoPriceOnly] = useState(false)
  const [mapLoading, setMapLoading] = useState(true)
  const [ymapsReady, setYmapsReady] = useState(false)
  const [selectedZoneIds, setSelectedZoneIds] = useState<Set<number>>(new Set())
  const selectedZoneIdsRef = useRef<Set<number>>(new Set())
  const preEditSelectionRef = useRef<Set<number> | null>(null) // Сохраняем выбор перед редактированием

  // Синхронизируем ref с state
  useEffect(() => {
    selectedZoneIdsRef.current = selectedZoneIds
  }, [selectedZoneIds])

  const [isCreatingGroup, setIsCreatingGroup] = useState(false)
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null)
  const [editingGroupName, setEditingGroupName] = useState('')
  const [newGroupName, setNewGroupName] = useState('')
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set())
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)

  // Функции для сворачивания групп
  const toggleGroupCollapse = useCallback((groupId: number) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }, [])

  const collapseAllGroups = useCallback(() => {
    const allGroupIds = new Set(zones.filter(z => z.groupId).map(z => z.groupId as number))
    setCollapsedGroups(allGroupIds)
  }, [zones])

  const expandAllGroups = useCallback(() => {
    setCollapsedGroups(new Set())
  }, [])

  // Refs для работы с картой
  const mapRef = useRef<HTMLDivElement>(null)
  const ymapsRef = useRef<any>(null)
  const mapInstanceRef = useRef<any>(null)
  const polygonsRef = useRef<Map<number, any>>(new Map())
  const drawingPolygonRef = useRef<any>(null)
  const editingPolygonRef = useRef<any>(null)
  const placemarksRef = useRef<any[]>([]) // Для хранения warning placemarks

  // Загрузка API Yandex Maps
  useEffect(() => {
    if (window.ymaps) {
      initMap()
      return
    }

    const apiKey = '20ab406b-3098-4bdd-98b5-de034b65e509'
    const script = document.createElement('script')
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=ru_RU&load=package.full`
    script.async = true
    script.onload = () => {
      window.ymaps.ready(() => {
        ymapsRef.current = window.ymaps
        setYmapsReady(true)
        initMap()
      })
    }
    script.onerror = () => {
      message.error('Ошибка загрузки Yandex Maps API')
      setMapLoading(false)
    }
    document.head.appendChild(script)

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Инициализация карты
  const initMap = useCallback(() => {
    if (!mapRef.current || !window.ymaps || mapInstanceRef.current) return

    try {
      const ymaps = window.ymaps

      // Центрируем на Рязань по умолчанию
      mapInstanceRef.current = new ymaps.Map(mapRef.current, {
        center: DEFAULT_CITIES['Рязань'],
        zoom: 11,
        controls: ['zoomControl', 'typeSelector', 'fullscreenControl'],
      })

      // Включаем редактирование полигонов
      mapInstanceRef.current.behaviors.enable('scrollZoom')

      setMapLoading(false)
    } catch (error) {
      console.error('Ошибка инициализации карты:', error)
      message.error('Ошибка инициализации карты')
      setMapLoading(false)
    }
  }, [])

  // Загрузка зон
  const loadZones = useCallback(async (keepSelection = false) => {
    try {
      setLoading(true)

      // Сохраняем текущий выбор если нужно (используем ref чтобы избежать циклической зависимости)
      const previousSelection = keepSelection ? new Set(selectedZoneIdsRef.current) : null

      // Получаем зоны из коллекции
      // Загружаем зоны и группы параллельно
      const [collectionResponse, groupsResponse] = await Promise.all([
        fetchAPI(
          '/api/collections/objects-grouped/delivery-prices-zones?access_key=axioma&limit=10&order_by=updated-at&order_dir=desc&page=0',
        ),
        fetchAPI('/api/collections/objects-groups/delivery-prices-zones'),
      ])

      // Извлекаем все зоны из сгруппированных данных
      const rawData = collectionResponse.data?.data || []
      const allZones: any[] = []

      rawData.forEach((item: any) => {
        if (item['is-group']) {
          // Это группа - добавляем только дочерние зоны, пропускаем саму группу
          if (item.children) {
            item.children.forEach((child: any) => {
              allZones.push({
                ...child,
                groupName: item.name,
              })
            })
          }
          // Не добавляем саму группу в список зон!
        } else {
          // Отдельная зона без группы
          allZones.push(item)
        }
      })

      // Извлекаем группы (даже пустые)
      // API groups возвращает {data: [...]} а не {data: {data: [...]}}
      const groupsData = groupsResponse.data || []
      // Преобразуем группы в массив объектов {id, name}
      const allGroups = groupsData
        .map((g: any) => ({
          id: g.id,
          name: g.name || g.data?.name,
        }))
        .filter((g: any) => g.name)

      // Fallback: если API групп вернул пусто, берем группы из зон
      if (allGroups.length === 0) {
        const groupNamesFromZones = new Map<number, string>()
        allZones.forEach((item: any) => {
          const groupId = item['group-id'] || item.groupId || item.data?.['group-id']
          const groupName = item['group-name'] || item.groupName
          if (groupId && groupName) groupNamesFromZones.set(groupId, groupName)
        })
        groupNamesFromZones.forEach((name, id) => allGroups.push({ id, name }))
      }

      setGroups(allGroups)

      const collectionZones: DeliveryZone[] = allZones.map((item: any) => {
        const groupName = item['group-name'] || item.groupName
        return {
          id: item.id,
          name: item.name || item.data?.name || 'Без названия',
          color:
            item.data?.attributes?.color || item.data?.attributes?.attributes?.color || '#1890ff',
          area: item.data?.attributes?.area || item.data?.attributes?.attributes?.area,
          price:
            item.data?.attributes?.price ||
            item.data?.attributes?.cost ||
            item.data?.attributes?.deliveryPrice ||
            item.data?.attributes?.attributes?.price,
          minPrice:
            item.data?.attributes?.['min-price'] ||
            item.data?.attributes?.attributes?.['min-price'],
          aocId: item.data?.id,
          groupId: item['group-id'] || item.groupId || item.data?.['group-id'],
          groupName: groupName, // Город/группа для группировки
          aocName: groupName, // Для обратной совместимости
        }
      })

      setZones(collectionZones)
      setFilteredZones(collectionZones)

      // Восстанавливаем предыдущий выбор или выбираем все
      if (keepSelection && previousSelection) {
        // Оставляем только те ID, которые существуют в новых зонах
        const validIds = new Set(
          Array.from(previousSelection).filter(id => collectionZones.some(z => z.id === id)),
        )
        // Если не осталось ни одного выбранного, выбираем все
        setSelectedZoneIds(validIds.size > 0 ? validIds : new Set(collectionZones.map(z => z.id)))
      } else {
        // Выбираем все зоны по умолчанию
        setSelectedZoneIds(new Set(collectionZones.map(z => z.id)))
      }

      // Отображаем зоны на карте
      displayZonesOnMap(collectionZones)
    } catch (error) {
      console.error('Ошибка загрузки зон:', error)
      message.error('Ошибка загрузки зон доставки')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Редактирование названия группы
  const handleEditGroup = useCallback((group: { id: number; name: string }) => {
    setEditingGroupId(group.id)
    setEditingGroupName(group.name)
  }, [])

  // Сохранение нового названия группы
  const handleSaveGroupName = useCallback(async () => {
    if (!editingGroupId || !editingGroupName.trim()) {
      message.error('Введите название группы')
      return
    }

    try {
      setLoading(true)
      await fetchAPI(`/api/collections/objects-groups/${editingGroupId}`, {
        method: 'POST',
        body: JSON.stringify({
          data: {
            name: editingGroupName.trim(),
            'collection-name': 'delivery-prices-zones',
          },
        }),
      })
      message.success('Группа обновлена')
      setEditingGroupId(null)
      setEditingGroupName('')
      await loadZones(true)
    } catch (error) {
      console.error('Ошибка обновления группы:', error)
      message.error('Ошибка обновления группы')
    } finally {
      setLoading(false)
    }
  }, [editingGroupId, editingGroupName, loadZones])

  // Отмена редактирования группы
  const handleCancelGroupEdit = useCallback(() => {
    setEditingGroupId(null)
    setEditingGroupName('')
  }, [])

  // Удаление группы
  const handleDeleteGroup = useCallback(
    async (group: { id: number; name: string }) => {
      // Проверяем, есть ли зоны в группе
      const zonesInGroup = filteredZones.filter(z => z.groupId === group.id)
      if (zonesInGroup.length > 0) {
        message.error(
          `Нельзя удалить группу с ${zonesInGroup.length} зонами. Сначала переместите или удалите зоны.`,
        )
        return
      }

      Modal.confirm({
        title: 'Удаление группы',
        content: `Вы уверены, что хотите удалить группу "${group.name}"?`,
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: async () => {
          try {
            setLoading(true)
            await fetchAPI(`/api/collections/objects-groups/${group.id}`, {
              method: 'DELETE',
            })
            message.success('Группа удалена')
            await loadZones(true)
          } catch (error) {
            console.error('Ошибка удаления группы:', error)
            message.error('Ошибка удаления группы')
          } finally {
            setLoading(false)
          }
        },
      })
    },
    [filteredZones, loadZones],
  )

  // Функции для работы с чекбоксами
  const toggleZoneSelection = useCallback((zoneId: number) => {
    setSelectedZoneIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(zoneId)) {
        newSet.delete(zoneId)
      } else {
        newSet.add(zoneId)
      }
      return newSet
    })
  }, [])

  const toggleGroupSelection = useCallback(
    (groupName: string, zonesInGroup: DeliveryZone[], isSelected: boolean) => {
      setSelectedZoneIds(prev => {
        const newSet = new Set(prev)
        zonesInGroup.forEach(zone => {
          if (isSelected) {
            newSet.delete(zone.id)
          } else {
            newSet.add(zone.id)
          }
        })
        return newSet
      })
    },
    [],
  )

  const selectAllZones = useCallback(() => {
    setSelectedZoneIds(new Set(filteredZones.map(z => z.id)))
  }, [filteredZones])

  const deselectAllZones = useCallback(() => {
    setSelectedZoneIds(new Set())
  }, [])

  // Отображение зон на карте
  const displayZonesOnMap = useCallback(
    (zonesToDisplay: DeliveryZone[], currentEditingId?: number | null) => {
      if (!mapInstanceRef.current || !window.ymaps) {
        return
      }
      const ymaps = window.ymaps

      // Ждем готовности карты
      ymaps.ready(() => {
        // Очищаем существующие placemarks (warning иконки)
        placemarksRef.current.forEach(placemark => {
          try {
            mapInstanceRef.current.geoObjects.remove(placemark)
          } catch (e) {
            // Игнорируем ошибки удаления
          }
        })
        placemarksRef.current = []

        // Очищаем существующие полигоны, кроме редактируемого
        polygonsRef.current.forEach((polygon, id) => {
          if (id === currentEditingId) {
            return // Не трогаем редактируемый полигон
          }
          try {
            mapInstanceRef.current.geoObjects.remove(polygon)
          } catch (e) {
            // Игнорируем ошибки удаления
          }
        })
        // Удаляем из Map все кроме редактируемого
        const editingPolygon = currentEditingId ? polygonsRef.current.get(currentEditingId) : null
        polygonsRef.current.clear()
        if (editingPolygon && currentEditingId) {
          polygonsRef.current.set(currentEditingId, editingPolygon)
        }

        // Показываем только выбранные зоны
        let skippedNoArea = 0
        let skippedNotSelected = 0
        let skippedNoCoords = 0
        let rendered = 0
        let errors = 0

        zonesToDisplay.forEach(zone => {
          if (!zone.area) {
            skippedNoArea++
            return
          }
          if (!selectedZoneIds.has(zone.id)) {
            skippedNotSelected++
            return
          }

          // Пропускаем если это редактируемый полигон (он уже на карте)
          if (zone.id === currentEditingId) {
            rendered++
            return
          }

          try {
            // Парсим WKT или GeoJSON
            const coordinates = parseAreaToCoordinates(zone.area)
            if (!coordinates || coordinates.length === 0) {
              skippedNoCoords++
              return
            }

            // Определяем, задан ли цвет (строка 'null' тоже считается отсутствием цвета)
            const hasColor =
              zone.color &&
              zone.color !== 'transparent' &&
              zone.color !== '' &&
              zone.color !== 'null'

            // Формируем подсказку с информацией о ценах
            const hasPrice = zone.price || zone.minPrice
            const priceInfo = hasPrice
              ? `Цена: ${zone.price ? zone.price + ' ₽' : '-'} / Мин: ${
                  zone.minPrice ? zone.minPrice + ' ₽' : '-'
                }`
              : '⚠️ Цена не установлена'

            const svgPattern =
              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjYWFhYWFhIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNhYWFhYWEiLz48L3N2Zz4='
            const polygonOptions: any = {
              strokeWidth: 2,
              strokeOpacity: 1,
              zIndex: 100,
              visible: true,
              // Если цвет задан - используем его, иначе - красный для теста
              strokeColor: hasColor ? zone.color + 'FF' : '#FF0000FF',
              fillColor: hasColor ? zone.color : undefined,
              fillImageHref: hasColor ? undefined : svgPattern,
              fillMethod: hasColor ? undefined : 'tile',
              fillOpacity: hasColor ? 0.4 : 0.6,
            }

            const polygon = new ymaps.Polygon(
              coordinates,
              {
                hintContent: `${zone.name}\n${priceInfo}`,
                balloonContent: `<b>${zone.name}</b><br/>${zone.aocName || ''}<br/>${priceInfo}`,
              },
              polygonOptions,
            )

            // Обработчики событий
            polygon.events.add('click', () => {
              setSelectedZoneId(zone.id)
            })

            polygon.events.add('mouseenter', () => {
              polygon.options.set({ fillOpacity: 0.7, strokeWidth: 4 })
            })

            polygon.events.add('mouseleave', () => {
              if (selectedZoneId !== zone.id) {
                polygon.options.set({ fillOpacity: 0.5, strokeWidth: 3 })
              }
            })

            // Добавляем через geoObjects с обработкой ошибок
            try {
              mapInstanceRef.current.geoObjects.add(polygon)
              polygonsRef.current.set(zone.id, polygon)
              rendered++

              // Если одна из цен не указана И зона выбрана - добавляем балун в центре зоны
              if ((!zone.price || !zone.minPrice) && selectedZoneIds.has(zone.id)) {
                // Вычисляем центр полигона (среднее координат)
                const bounds = polygon.geometry.getBounds()
                if (bounds) {
                  const center = [
                    (bounds[0][0] + bounds[1][0]) / 2,
                    (bounds[0][1] + bounds[1][1]) / 2,
                  ]
                  const bothMissing = !zone.price && !zone.minPrice
                  const missingPrice = !zone.price ? 'Цена' : 'Минимальная цена'
                  const warningPlacemark = new ymaps.Placemark(
                    center,
                    {
                      hintContent: bothMissing
                        ? '⚠️ Цена и минимальная цена не указаны'
                        : `⚠️ Не указана ${missingPrice.toLowerCase()}`,
                      balloonContent: bothMissing
                        ? `<b>${zone.name}</b><br/>⚠️ Цена и минимальная цена не указаны`
                        : `<b>${zone.name}</b><br/>⚠️ Не указана: ${missingPrice}`,
                    },
                    {
                      preset: bothMissing ? 'islands#attentionIcon' : 'islands#yellowAttentionIcon',
                      iconColor: bothMissing ? '#FF4D4F' : '#FFC107',
                      zIndex: 200,
                    },
                  )
                  mapInstanceRef.current.geoObjects.add(warningPlacemark)
                  placemarksRef.current.push(warningPlacemark)
                }
              }
            } catch (addError) {
              console.error(`Failed to add polygon for ${zone.name}:`, addError)
              errors++
            }
          } catch (error) {
            errors++
            console.error(`❌ Ошибка отображения зоны ${zone.id}:`, error)
          }
        })

        // Центрируем карту на всех отрендеренных зонах
        if (rendered > 0 && mapInstanceRef.current) {
          try {
            const bounds = mapInstanceRef.current.geoObjects.getBounds()
            if (bounds) {
              mapInstanceRef.current.setBounds(bounds, { checkZoomRange: true, duration: 300 })
            }
          } catch (boundsError) {
            console.error('Failed to center map:', boundsError)
          }
        }
      })
    },
    [selectedZoneId, selectedZoneIds],
  )

  // Ref для отслеживания редактируемого полигона (используем ref чтобы не триггерить перерисовку)
  const editingPolygonIdRef = useRef<number | null>(null)

  // Загрузка при монтировании
  useEffect(() => {
    if (ymapsReady) {
      loadZones()
    }
  }, [ymapsReady, loadZones])

  // Фильтрация зон
  useEffect(() => {
    let filtered = zones

    // Фильтр по поисковому запросу
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        zone =>
          zone.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          zone.aocName?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    }

    // Фильтр "Зоны без цены"
    if (showNoPriceOnly) {
      filtered = filtered.filter(zone => !zone.price)
    }

    setFilteredZones(filtered)
  }, [searchQuery, zones, showNoPriceOnly])

  // Группировка зон для отображения (иерархическая структура)
  const groupedZones = useMemo(() => {
    // Создаем Map групп из загруженного списка (по ID, включая пустые)
    const groupMap = new Map<number, DeliveryZone[]>()
    const groupIdToName = new Map<number, string>()

    // Инициализируем все группы (даже пустые)
    groups.forEach(group => {
      groupMap.set(group.id, [])
      groupIdToName.set(group.id, group.name)
    })

    const noGroup: DeliveryZone[] = []

    // Распределяем зоны по группам
    filteredZones.forEach(zone => {
      if (zone.groupId && groupMap.has(zone.groupId)) {
        const groupZones = groupMap.get(zone.groupId)
        if (groupZones) {
          groupZones.push(zone)
        }
      } else if (zone.groupId) {
        // Зона ссылается на несуществующую группу - добавляем в корень
        noGroup.push(zone)
      } else {
        noGroup.push(zone)
      }
    })

    const result: Array<
      | { type: 'group'; groupName: string; groupId: number; level: number; isCollapsed: boolean }
      | { type: 'zone'; zone: DeliveryZone; level: number }
    > = []

    // Сначала группы (в порядке из API) - level=0
    groups.forEach(group => {
      const isCollapsed = collapsedGroups.has(group.id)
      result.push({
        type: 'group',
        groupName: group.name,
        groupId: group.id,
        level: 0,
        isCollapsed,
      })
      // Добавляем зоны только если группа не свёрнута - level=1
      const zones = groupMap.get(group.id) || []
      if (!isCollapsed) {
        zones.forEach(zone => result.push({ type: 'zone', zone, level: 1 }))
      }
    })

    // Зоны без группы в конце (level=0)
    noGroup.forEach(zone => result.push({ type: 'zone', zone, level: 0 }))

    return result
  }, [filteredZones, collapsedGroups, groups])

  // Подсветка выбранной зоны
  useEffect(() => {
    polygonsRef.current.forEach((polygon, id) => {
      const isSelected = id === selectedZoneId
      polygon.options.set({
        fillOpacity: isSelected ? 0.5 : 0.3,
        strokeWidth: isSelected ? 4 : 2,
      })
    })

    // Центрируем карту на выбранной зоне
    if (selectedZoneId && polygonsRef.current.has(selectedZoneId)) {
      const polygon = polygonsRef.current.get(selectedZoneId)
      const bounds = polygon.geometry.getBounds()
      mapInstanceRef.current?.setBounds(bounds, { checkZoomRange: true, duration: 300 })
    }
  }, [selectedZoneId])

  // Начало создания новой зоны
  const handleStartDrawing = useCallback(() => {
    if (!mapInstanceRef.current || !window.ymaps) return

    setIsDrawing(true)
    setSelectedZoneId(null)

    const ymaps = window.ymaps

    // Создаем пустой полигон для рисования
    drawingPolygonRef.current = new ymaps.Polygon(
      [[]],
      {},
      {
        fillColor: '#1890ff',
        strokeColor: '#096dd9',
        strokeWidth: 2,
        fillOpacity: 0.3,
        editorDrawingCursor: 'crosshair',
        editorMaxPoints: 100,
      },
    )

    mapInstanceRef.current.geoObjects.add(drawingPolygonRef.current)

    // Включаем редактор
    const editor = drawingPolygonRef.current.editor
    editor.startDrawing()

    // Обработчик завершения рисования
    editor.events.add('drawingstop', () => {
      const coordinates = drawingPolygonRef.current.geometry.getCoordinates()
      if (coordinates[0]?.length > 2) {
        // Открываем модалку для ввода названия
        setEditingZone({
          id: Date.now(), // Временный ID
          name: '',
          color: '#1890ff',
          isNew: true,
        })
      } else {
        // Удаляем если недостаточно точек
        mapInstanceRef.current.geoObjects.remove(drawingPolygonRef.current)
        drawingPolygonRef.current = null
      }
      setIsDrawing(false)
    })
  }, [])

  // Отмена рисования
  const handleCancelDrawing = useCallback(() => {
    if (drawingPolygonRef.current && mapInstanceRef.current) {
      drawingPolygonRef.current.editor.stopDrawing()
      mapInstanceRef.current.geoObjects.remove(drawingPolygonRef.current)
      drawingPolygonRef.current = null
    }
    setIsDrawing(false)
  }, [])

  // Редактирование геометрии полигона на карте
  const [isEditingGeometry, setIsEditingGeometry] = useState(false)

  // Обновляем отображение на карте при изменении выбранных зон
  // Не обновляем если в режиме редактирования геометрии (чтобы не сбивать фокус)
  useEffect(() => {
    if (isEditingGeometry) {
      return
    }
    const timer = setTimeout(() => {
      displayZonesOnMap(filteredZones, editingPolygonIdRef.current)
    }, 500)
    return () => clearTimeout(timer)
  }, [selectedZoneIds, displayZonesOnMap, filteredZones, isEditingGeometry])

  const handleEditPolygonGeometry = useCallback((zone: DeliveryZone) => {
    // Функция для активации редактирования с проверкой наличия полигона
    const activateEditing = () => {
      if (!polygonsRef.current.has(zone.id)) {
        // Полигон еще не создан, пробуем снова через 100ms
        return false
      }

      // Останавливаем редактирование предыдущего полигона
      if (editingPolygonIdRef.current && polygonsRef.current.has(editingPolygonIdRef.current)) {
        const prevPolygon = polygonsRef.current.get(editingPolygonIdRef.current)
        prevPolygon.editor.stopEditing()
      }

      // Включаем редактирование нового полигона
      const polygon = polygonsRef.current.get(zone.id)
      polygon.editor.startEditing()
      editingPolygonRef.current = polygon
      editingPolygonIdRef.current = zone.id
      setIsEditingGeometry(true)
      setSelectedZoneId(zone.id)

      // Центрируем карту на полигоне с отступами
      const bounds = polygon.geometry.getBounds()
      if (bounds && mapInstanceRef.current) {
        mapInstanceRef.current.setBounds(bounds, {
          duration: 300,
          checkZoomRange: true,
          zoomMargin: 50,
        })
      }

      message.info(
        'Перетаскивайте точки полигона для редактирования. Нажмите "Сохранить геометрию" когда закончите.',
      )
      return true
    }

    // Пробуем активировать сразу
    if (activateEditing()) {
      return
    }

    // Если не получилось - пробуем с задержкой до 5 попыток
    let attempts = 0
    const maxAttempts = 10
    const interval = setInterval(() => {
      attempts++
      if (activateEditing()) {
        clearInterval(interval)
      } else if (attempts >= maxAttempts) {
        clearInterval(interval)
        message.error('Полигон не найден на карте')
      }
    }, 100)
  }, [])

  // Отмена редактирования геометрии
  const handleCancelPolygonEdit = useCallback(() => {
    if (editingPolygonRef.current) {
      editingPolygonRef.current.editor.stopEditing()
      editingPolygonRef.current = null
    }
    editingPolygonIdRef.current = null
    setIsEditingGeometry(false)
    // Перезагружаем зоны чтобы вернуть оригинальную геометрию, сохраняя выделение
    loadZones(true)
  }, [loadZones])

  // Inline редактирование зоны
  const [editingZoneId, setEditingZoneId] = useState<number | null>(null)
  const [editingZoneData, setEditingZoneData] = useState<DeliveryZone | null>(null)

  const handleEditZone = useCallback(
    (zone: DeliveryZone) => {
      // Сохраняем текущий выбор перед редактированием
      preEditSelectionRef.current = new Set(selectedZoneIds)
      // Если зона не выделена - выделяем её
      if (!selectedZoneIds.has(zone.id)) {
        setSelectedZoneIds(prev => new Set([...prev, zone.id]))
      }
      setEditingZoneId(zone.id)
      setEditingZoneData({ ...zone })
      setSelectedZoneId(zone.id)
      // Автоматически активируем редактирование геометрии на карте
      handleEditPolygonGeometry(zone)
    },
    [handleEditPolygonGeometry, selectedZoneIds],
  )

  const handleCancelInlineEdit = useCallback(() => {
    setEditingZoneId(null)
    setEditingZoneData(null)
    // Останавливаем редактирование геометрии (loadZones(true) сохранит текущий выбор)
    handleCancelPolygonEdit()
    // Не восстанавливаем preEditSelectionRef - оставляем текущий выбор
    // (включает редактируемую зону, которая была добавлена при входе)
    preEditSelectionRef.current = null
  }, [handleCancelPolygonEdit])

  const handleSaveInlineEdit = useCallback(async () => {
    if (!editingZoneData || !editingZoneData.name.trim()) {
      message.error('Введите название зоны')
      return
    }

    try {
      setLoading(true)

      let area: string | undefined

      // Получаем координаты из редактируемого полигона
      if (editingPolygonRef.current) {
        const coords = editingPolygonRef.current.geometry.getCoordinates()
        area = coords[0].map((c: number[]) => `${c[0]},${c[1]}`).join('|')
      }

      const zoneData = {
        name: editingZoneData.name,
        'group-name': editingZoneData.groupName || null,
        data: {
          id: editingZoneData.aocId || editingZoneData.id,
          'group-id': editingZoneData.groupId || null,
          name: editingZoneData.name,
          attributes: {
            color: editingZoneData.color,
            area: area,
            aocName: editingZoneData.aocName,
            price: editingZoneData.price,
            'min-price': editingZoneData.minPrice,
          },
          metadata: {
            schema: ['delivery-prices-zones'],
          },
        },
      }

      await fetchAPI(`/api/collections/delivery-prices-zones/objects/${editingZoneId}`, {
        method: 'POST',
        body: JSON.stringify(zoneData),
      })

      // Останавливаем редактирование геометрии
      if (editingPolygonRef.current) {
        editingPolygonRef.current.editor.stopEditing()
        editingPolygonRef.current = null
      }
      editingPolygonIdRef.current = null
      setIsEditingGeometry(false)

      message.success('Зона сохранена')
      setEditingZoneId(null)
      setEditingZoneData(null)
      // Не восстанавливаем старый выбор - оставляем текущий (с редактируемой зоной)
      preEditSelectionRef.current = null
      await loadZones(true) // Сохраняем выделенные зоны
    } catch (error) {
      console.error('Ошибка сохранения зоны:', error)
      message.error('Ошибка сохранения зоны')
    } finally {
      setLoading(false)
    }
  }, [editingZoneData, editingZoneId, loadZones])

  // Сохранение зоны
  const handleSaveZone = useCallback(async () => {
    if (!editingZone || !editingZone.name.trim()) {
      message.error('Введите название зоны')
      return
    }

    try {
      let area: string | undefined

      // Получаем координаты из редактируемого полигона
      if (editingPolygonRef.current) {
        const coords = editingPolygonRef.current.geometry.getCoordinates()
        // Формат Schedex: lat,lon|lat,lon|...
        area = coords[0].map((c: number[]) => `${c[0]},${c[1]}`).join('|')
        editingPolygonRef.current.editor.stopEditing()
        editingPolygonRef.current = null
      } else if (drawingPolygonRef.current) {
        const coords = drawingPolygonRef.current.geometry.getCoordinates()
        // Формат Schedex: lat,lon|lat,lon|...
        area = coords[0].map((c: number[]) => `${c[0]},${c[1]}`).join('|')
        mapInstanceRef.current?.geoObjects.remove(drawingPolygonRef.current)
        drawingPolygonRef.current = null
      }

      const zoneData = {
        name: editingZone.name,
        'group-name': null,
        data: {
          id: editingZone.aocId || editingZone.id,
          name: editingZone.name,
          attributes: {
            color: editingZone.color,
            area: area,
            aocName: editingZone.aocName,
          },
          metadata: {
            schema: ['delivery-prices-zones'],
          },
        },
      }

      let newZoneId: number | null = null

      if (editingZone.isNew) {
        // Создание новой зоны
        const response = await fetchAPI('/api/collections/delivery-prices-zones', {
          method: 'POST',
          body: JSON.stringify(zoneData),
        })
        message.success('Зона создана')
        // Запоминаем ID новой зоны
        newZoneId = response?.data?.id || response?.id || null
      } else {
        // Обновление существующей
        await fetchAPI(`/api/collections/delivery-prices-zones/objects/${editingZone.id}`, {
          method: 'PUT',
          body: JSON.stringify(zoneData),
        })
        message.success('Зона обновлена')
      }

      setEditingZone(null)
      await loadZones(true) // Сохраняем выделенные зоны

      // Выделяем новую зону чекбоксом
      if (newZoneId) {
        setSelectedZoneIds(prev => {
          const newSet = new Set(Array.from(prev))
          newSet.add(newZoneId as number)
          return newSet
        })
      }
    } catch (error) {
      console.error('Ошибка сохранения:', error)
      message.error('Ошибка сохранения зоны')
    }
  }, [editingZone, loadZones])

  // Создание группы
  const handleCreateGroup = useCallback(async () => {
    if (!newGroupName.trim()) {
      message.error('Введите название группы')
      return
    }

    try {
      setLoading(true)
      await fetchAPI('/api/collections/objects-groups/', {
        method: 'POST',
        body: JSON.stringify({
          data: {
            name: newGroupName.trim(),
            'collection-name': 'delivery-prices-zones',
          },
        }),
      })

      message.success('Группа создана')
      setIsCreatingGroup(false)
      setNewGroupName('')
      await loadZones()
    } catch (error) {
      console.error('Ошибка создания группы:', error)
      message.error('Ошибка создания группы')
    } finally {
      setLoading(false)
    }
  }, [newGroupName, loadZones])

  // Удаление зоны
  const handleDeleteZone = useCallback(
    async (zoneId: number) => {
      Modal.confirm({
        title: 'Удалить зону?',
        content: 'Это действие нельзя отменить',
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: async () => {
          try {
            await fetchAPI(`/api/collections/objects/${zoneId}`, {
              method: 'DELETE',
            })

            // Удаляем с карты
            if (polygonsRef.current.has(zoneId)) {
              mapInstanceRef.current?.geoObjects.remove(polygonsRef.current.get(zoneId))
              polygonsRef.current.delete(zoneId)
            }

            message.success('Зона удалена')
            await loadZones()
          } catch (error) {
            message.error('Ошибка удаления зоны')
          }
        },
      })
    },
    [loadZones],
  )

  // Перемещение зоны между группами
  const handleMoveZoneToGroup = useCallback(
    async (zone: DeliveryZone, newGroupId: number | null, newGroupName: string | null) => {
      try {
        setLoading(true)

        // Парсим area из текущей зоны
        let area = zone.area || ''
        if (area.startsWith('lat,lon|')) {
          area = area.substring(8)
        }

        const zoneData = {
          name: zone.name,
          'group-name': newGroupName,
          data: {
            id: zone.aocId || zone.id,
            'group-id': newGroupId,
            name: zone.name,
            attributes: {
              color: zone.color,
              area: area,
              aocName: newGroupName || zone.aocName,
              price: zone.price,
              'min-price': zone.minPrice,
            },
            metadata: {
              schema: ['delivery-prices-zones'],
            },
          },
        }

        await fetchAPI(`/api/collections/delivery-prices-zones/objects/${zone.id}`, {
          method: 'POST',
          body: JSON.stringify(zoneData),
        })

        message.success(`Зона перемещена в ${newGroupName || 'корень'}`)
        await loadZones(true) // Сохраняем выделение
      } catch (error) {
        console.error('Ошибка перемещения зоны:', error)
        message.error('Ошибка перемещения зоны')
      } finally {
        setLoading(false)
      }
    },
    [loadZones],
  )

  // Отмена редактирования
  const handleCancelEdit = useCallback(() => {
    if (editingPolygonRef.current) {
      editingPolygonRef.current.editor.stopEditing()
      editingPolygonRef.current = null
    }
    if (drawingPolygonRef.current) {
      mapInstanceRef.current?.geoObjects.remove(drawingPolygonRef.current)
      drawingPolygonRef.current = null
    }
    setEditingZone(null)
    setIsDrawing(false)
  }, [])

  const breadcrumbs = [
    { url: '/admin', title: 'Admin' },
    { url: '/admin/delivery-zones', title: 'Зоны доставки' },
  ]

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      <PageContainer>
        {/* Боковая панель */}
        <Sidebar>
          <Card size="small" style={{ marginBottom: 0 }}>
            <Toolbar>
              <Tooltip title="Создать новую зону">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleStartDrawing}
                  disabled={isDrawing}
                >
                  Создать зону
                </Button>
              </Tooltip>
              <Tooltip title="Создать группу">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setIsCreatingGroup(true)}
                  disabled={isDrawing}
                >
                  Создать группу
                </Button>
              </Tooltip>
            </Toolbar>

            <SearchWrapper>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Поиск по названию..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                allowClear
              />
            </SearchWrapper>

            <div style={{ padding: '8px 4px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Switch
                size="small"
                checked={showNoPriceOnly}
                onChange={checked => setShowNoPriceOnly(checked)}
              />
              <span style={{ fontSize: 13, color: '#595959' }}>Только зоны без цены</span>
              {showNoPriceOnly && (
                <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 'auto' }}>
                  {filteredZones.length} зон
                </span>
              )}
            </div>
          </Card>

          <Card
            size="small"
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Зоны доставки ({filteredZones.length})</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  <Tooltip title="Выбрать все">
                    <Button size="small" icon={<CheckSquareOutlined />} onClick={selectAllZones} />
                  </Tooltip>
                  <Tooltip title="Снять все">
                    <Button size="small" icon={<BorderOutlined />} onClick={deselectAllZones} />
                  </Tooltip>
                  <Tooltip title="Свернуть все">
                    <Button size="small" icon={<CompressOutlined />} onClick={collapseAllGroups} />
                  </Tooltip>
                  <Tooltip title="Развернуть все">
                    <Button size="small" icon={<ExpandOutlined />} onClick={expandAllGroups} />
                  </Tooltip>
                  <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 4 }}>
                    {selectedZoneIds.size} выбрано
                  </span>
                </div>
              </div>
            }
            style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flex: 1, overflow: 'auto', padding: '8px 0' }}
          >
            <List
              dataSource={groupedZones}
              loading={loading}
              locale={{
                emptyText: (
                  <EmptyState>
                    <GlobalOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <div>Нет зон доставки</div>
                    <div style={{ fontSize: 12, marginTop: 8 }}>Создайте новую зону</div>
                  </EmptyState>
                ),
              }}
              renderItem={(
                item:
                  | {
                      type: 'group'
                      groupName: string
                      groupId: number
                      level: number
                      isCollapsed: boolean
                    }
                  | { type: 'zone'; zone: DeliveryZone; level: number },
              ) => {
                if (item.type === 'group') {
                  // Находим все зоны группы по ID
                  const groupZones = filteredZones.filter(z => z.groupId === item.groupId)
                  const selectedCount = groupZones.filter(z => selectedZoneIds.has(z.id)).length
                  const isIndeterminate = selectedCount > 0 && selectedCount < groupZones.length
                  const isChecked = selectedCount === groupZones.length && groupZones.length > 0

                  return (
                    <div
                      key={`group-${item.groupId}`}
                      draggable={true}
                      onDragOver={e => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onDrop={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        const zoneId = parseInt(e.dataTransfer.getData('zoneId'))
                        const zone = filteredZones.find(z => z.id === zoneId)
                        if (zone && zone.groupId !== item.groupId) {
                          // Используем groupId напрямую из группы
                          handleMoveZoneToGroup(zone, item.groupId, item.groupName)
                        }
                      }}
                      onMouseEnter={() => setHoveredItemId(`group-${item.groupId}`)}
                      onMouseLeave={() => setHoveredItemId(null)}
                      style={{
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#262626',
                        borderBottom: '1px solid #e8e8e8',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleGroupCollapse(item.groupId)}
                    >
                      {item.isCollapsed ? (
                        <CaretRightOutlined style={{ color: '#8c8c8c' }} />
                      ) : (
                        <CaretDownOutlined style={{ color: '#8c8c8c' }} />
                      )}
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isChecked}
                        onChange={() => toggleGroupSelection(item.groupName, groupZones, isChecked)}
                        onClick={e => e.stopPropagation()}
                      />
                      {editingGroupId === item.groupId ? (
                        <Input
                          value={editingGroupName}
                          onChange={e => setEditingGroupName(e.target.value)}
                          size="small"
                          style={{ width: 150 }}
                          autoFocus
                          onPressEnter={handleSaveGroupName}
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <span>{item.groupName}</span>
                      )}
                      <span
                        style={{
                          fontSize: 11,
                          color: '#8c8c8c',
                          marginLeft: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {editingGroupId === item.groupId ? (
                          <>
                            <Button
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={e => {
                                e.stopPropagation()
                                handleSaveGroupName()
                              }}
                            />
                            <Button
                              size="small"
                              icon={<CloseOutlined />}
                              onClick={e => {
                                e.stopPropagation()
                                handleCancelGroupEdit()
                              }}
                            />
                          </>
                        ) : (
                          hoveredItemId === `group-${item.groupId}` && (
                            <>
                              <Tooltip title="Редактировать группу">
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<EditOutlined />}
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleEditGroup({ id: item.groupId, name: item.groupName })
                                  }}
                                  style={{ height: 0 }}
                                />
                              </Tooltip>
                              <Tooltip title="Удалить группу">
                                <Button
                                  size="small"
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleDeleteGroup({ id: item.groupId, name: item.groupName })
                                  }}
                                  style={{ height: 0 }}
                                />
                              </Tooltip>
                            </>
                          )
                        )}
                        <span style={{ marginLeft: 4 }}>
                          {selectedCount}/{groupZones.length}
                        </span>
                      </span>
                    </div>
                  )
                }
                const zone = item.zone
                const isZoneSelected = selectedZoneIds.has(zone.id)
                const isEditingThisZone = editingZoneId === zone.id
                const isEditingAnyZone = editingZoneId !== null

                // Если редактируем другую зону - показываем заблокированный элемент
                if (isEditingAnyZone && !isEditingThisZone) {
                  return (
                    <ZoneListItem
                      key={zone.id}
                      $isActive={false}
                      $color={zone.color}
                      style={{
                        paddingLeft: item.level === 1 ? 24 : 8,
                        opacity: 0.5,
                        pointerEvents: 'none',
                      }}
                    >
                      <Checkbox checked={isZoneSelected} disabled />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <ZoneHeader>
                          <ZoneName title={zone.name}>{zone.name}</ZoneName>
                        </ZoneHeader>
                        {zone.price ? (
                          <ZoneMeta style={{ color: '#52c41a' }}>Цена: {zone.price} ₽</ZoneMeta>
                        ) : (
                          <ZoneMeta style={{ color: '#ff4d4f' }}>Цена не установлена</ZoneMeta>
                        )}
                      </div>
                    </ZoneListItem>
                  )
                }

                // Inline редактор для текущей зоны
                if (isEditingThisZone && editingZoneData) {
                  return (
                    <ZoneListItem
                      key={zone.id}
                      $isActive={true}
                      $color={editingZoneData.color}
                      style={{
                        paddingLeft: item.level === 1 ? 34 : 20,
                        background: '#fff7e6 !important',
                      }}
                    >
                      <div style={{ flex: 1, width: '100%' }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Input
                              value={editingZoneData.name}
                              onChange={e =>
                                setEditingZoneData({ ...editingZoneData, name: e.target.value })
                              }
                              placeholder="Название зоны"
                              size="small"
                              style={{ flex: 1 }}
                            />
                            <ColorPicker
                              value={editingZoneData.color}
                              onChange={(color: Color) =>
                                setEditingZoneData({
                                  ...editingZoneData,
                                  color: color.toHexString(),
                                })
                              }
                              size="small"
                            />
                          </div>
                          {/* Поля цен */}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <label
                                style={{
                                  fontSize: 11,
                                  color: '#8c8c8c',
                                  display: 'block',
                                  marginBottom: 2,
                                }}
                              >
                                Цена доставки (₽)
                              </label>
                              <Input
                                type="number"
                                value={editingZoneData.price || ''}
                                onChange={e =>
                                  setEditingZoneData({
                                    ...editingZoneData,
                                    price: e.target.value ? parseFloat(e.target.value) : undefined,
                                  })
                                }
                                placeholder="0"
                                size="small"
                                style={{ width: '100%' }}
                                prefix="₽"
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label
                                style={{
                                  fontSize: 11,
                                  color: '#8c8c8c',
                                  display: 'block',
                                  marginBottom: 2,
                                }}
                              >
                                Мин. цена (₽)
                              </label>
                              <Input
                                type="number"
                                value={editingZoneData.minPrice || ''}
                                onChange={e =>
                                  setEditingZoneData({
                                    ...editingZoneData,
                                    minPrice: e.target.value
                                      ? parseFloat(e.target.value)
                                      : undefined,
                                  })
                                }
                                placeholder="0"
                                size="small"
                                style={{ width: '100%' }}
                                prefix="₽"
                              />
                            </div>
                          </div>

                          {editingZoneData.aocName && (
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                              Город: {editingZoneData.aocName}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Button size="small" onClick={handleCancelInlineEdit}>
                              Отмена
                            </Button>
                            <Button
                              type="primary"
                              size="small"
                              icon={<SaveOutlined />}
                              onClick={handleSaveInlineEdit}
                              loading={loading}
                            >
                              Сохранить
                            </Button>
                          </div>
                        </Space>
                      </div>
                    </ZoneListItem>
                  )
                }

                // Обычный отображение зоны
                return (
                  <ZoneListItem
                    key={zone.id}
                    draggable={true}
                    onDragStart={e => {
                      e.dataTransfer.setData('zoneId', zone.id.toString())
                    }}
                    $isActive={selectedZoneId === zone.id}
                    $color={zone.color}
                    onClick={() => setSelectedZoneId(zone.id)}
                    onMouseEnter={() => setHoveredItemId(`zone-${zone.id}`)}
                    onMouseLeave={() => setHoveredItemId(null)}
                    style={{ paddingLeft: item.level === 1 ? 24 : 10 }}
                  >
                    <Tooltip title="Перетащить">
                      <MenuOutlined
                        style={{ cursor: 'grab', color: '#8c8c8c', fontSize: '12px' }}
                        onClick={e => e.stopPropagation()}
                      />
                    </Tooltip>
                    <Checkbox
                      checked={isZoneSelected}
                      onChange={() => toggleZoneSelection(zone.id)}
                      onClick={e => e.stopPropagation()}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ZoneHeader>
                        <ZoneName title={zone.name}>{zone.name}</ZoneName>
                        {(zone.price || zone.minPrice) && (
                          <ZoneMeta style={{ color: '#52c41a' }}>
                            {zone.price ? `${zone.price} ₽` : ''}
                            {zone.price && zone.minPrice ? ' / ' : ''}
                            {zone.minPrice ? `мин. ${zone.minPrice} ₽` : ''}
                          </ZoneMeta>
                        )}
                      </ZoneHeader>
                    </div>

                    {hoveredItemId === `zone-${zone.id}` && (
                      <ActionButtons size="small">
                        <Tooltip title="Редактировать зону">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={e => {
                              e.stopPropagation()
                              handleEditZone(zone)
                            }}
                            style={{ height: 0 }}
                          />
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={e => {
                              e.stopPropagation()
                              handleDeleteZone(zone.id)
                            }}
                            style={{ height: 0 }}
                          />
                        </Tooltip>
                      </ActionButtons>
                    )}
                  </ZoneListItem>
                )
              }}
            />
          </Card>
        </Sidebar>

        {/* Карта */}
        <MapContainer>
          {mapLoading && (
            <MapLoadingOverlay>
              <Spin size="large" tip="Загрузка карты..." />
            </MapLoadingOverlay>
          )}

          {isDrawing && (
            <DrawingHint>
              <GlobalOutlined />
              Кликайте на карте для создания полигона. Двойной клик для завершения.
              <Button
                type="link"
                style={{ color: 'white', padding: 0, marginLeft: 8 }}
                onClick={handleCancelDrawing}
              >
                Отмена
              </Button>
            </DrawingHint>
          )}

          {isEditingGeometry && (
            <EditingHint>
              <EditOutlined />
              Редактирование полигона. Перетаскивайте точки.
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                style={{ marginLeft: 8 }}
                onClick={() => {
                  // Обновляем area из полигона перед сохранением
                  if (editingPolygonRef.current && editingZoneData) {
                    const coords = editingPolygonRef.current.geometry.getCoordinates()
                    const area = coords[0].map((c: number[]) => `${c[0]},${c[1]}`).join('|')
                    setEditingZoneData({ ...editingZoneData, area })
                  }
                  handleSaveInlineEdit()
                }}
              >
                Сохранить
              </Button>
              <Button
                type="link"
                size="small"
                style={{ color: 'white', marginLeft: 8 }}
                onClick={handleCancelInlineEdit}
              >
                Отмена
              </Button>
            </EditingHint>
          )}

          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </MapContainer>
      </PageContainer>

      {/* Модалка создания новой зоны (только для isNew) */}
      <Modal
        title="Новая зона доставки"
        open={!!editingZone && editingZone.isNew}
        onOk={handleSaveZone}
        onCancel={handleCancelEdit}
        okText="Сохранить"
        cancelText="Отмена"
        destroyOnHidden
      >
        {editingZone && editingZone.isNew && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Название зоны
              </label>
              <Input
                value={editingZone.name}
                onChange={e => setEditingZone({ ...editingZone, name: e.target.value })}
                placeholder="Например: Центр города"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Цвет на карте
              </label>
              <ColorPicker
                value={editingZone.color}
                onChange={(color: Color) =>
                  setEditingZone({ ...editingZone, color: color.toHexString() })
                }
                showText
              />
            </div>

            {editingZone.aocName && (
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Город</label>
                <Input value={editingZone.aocName} disabled />
              </div>
            )}

            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              Нарисуйте полигон на карте, затем нажмите Сохранить
            </div>
          </Space>
        )}
      </Modal>

      {/* Модальное окно создания группы */}
      <Modal
        title="Создание группы"
        open={isCreatingGroup}
        onOk={handleCreateGroup}
        onCancel={() => {
          setIsCreatingGroup(false)
          setNewGroupName('')
        }}
        okText="Создать"
        cancelText="Отмена"
      >
        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Название группы
          </label>
          <Input
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            placeholder="Например: Москва"
            onPressEnter={handleCreateGroup}
          />
        </div>
      </Modal>
    </B2BLayout>
  )
}

// Добавляем тип для window
declare global {
  interface Window {
    ymaps: any
  }
}

export default DeliveryZonesEditor
